/**
 * ================================================================================================
 *  WHAT THE ASSISTANT IS ALLOWED TO SAY. **Section 5, 2026-10-26.**
 * ================================================================================================
 *
 * The Owner approved the support assistant and named its sources:
 *
 *   `I_GUIDE_BASELINE_SUPPORT_CHATBOT.md`   view 2 — symptom → classification → resolution,
 *                                           **nine classes**, 35 symptoms
 *   `J_CHATBOT_DIAGNOSTIC_CONTRACT.md` + ADDENDUM-3
 *                                           what must be HELD before `BUG_CANDIDATE` may be said
 *   `K_KNOWLEDGE_LIFECYCLE.md`              how that knowledge is kept current
 *
 * all at baseline `d5e76f3a77ea8a8a23a433231dbcb00401a2f035`.
 *
 * ── THIS FILE IS THE KNOWLEDGE, AND IT IS A TABLE ───────────────────────────────────────────────
 *
 * **There is no language model here and no free-text answering.** That is a deliberate boundary,
 * not a shortcut: J.0 permits a bot to answer the eight non-bug classes *"using view 2's rows"*,
 * and a table of those rows is exactly that — answerable, reviewable, and incapable of inventing a
 * sentence about a system it has not observed. Free-text Q&A needs a provider, a knowledge store
 * and a decision about what customer text leaves the estate; that is the Owner's call and it is
 * written up in the delivery note rather than assumed here.
 *
 * ── THREE RULES THE CONTENT OBEYS, FROM §J ──────────────────────────────────────────────────────
 *
 * 1. **It never says `BUG_CANDIDATE`.** J.2 lists the inspection points a bot must HOLD first — an
 *    audit row, a journal window, a release identity — and this panel holds none of them. The
 *    honest answer in its place is *"this is not something I can classify"*, with the escalation.
 * 2. **It never claims it can see what happened.** It can name the support reference code and say
 *    where it goes. It cannot read the audit table.
 * 3. **An absent entry is said out loud.** J.0 rule 3: *"when a REQUIRED point cannot be obtained,
 *    the bot must say what it could not obtain and stop."* A screen with no entry produces the
 *    no-answer state, never a plausible paragraph.
 *
 * ── AND IT RESPECTS PERMISSIONS ─────────────────────────────────────────────────────────────────
 *
 * J.3: a bot *"may never read or emit another tenant's data, and may never read anything a human in
 * the same conversation would not be permitted to read."* Entries that describe an action carry the
 * capability that action needs, and the panel hides them when the session does not hold it —
 * telling somebody how to perform an action the gate will refuse is a support answer that wastes
 * their afternoon.
 */

import { routes } from '../routes';

/** One sentence, in both languages. Entries are self-contained rather than catalogue keys: the
 *  knowledge is a table that is reviewed as a whole, and splitting it across two files would make
 *  the review two files. */
export interface Bilingual {
  readonly he: string;
  readonly en: string;
}

/**
 * The nine classes of `I_GUIDE_BASELINE_SUPPORT_CHATBOT.md` §0.1, verbatim in meaning.
 *
 * They are here because the obligation differs per class and the person reading needs to know
 * WHICH KIND of problem they have: `PERMISSION/ROLE` is resolved by their own office,
 * `OPERATIONS/INFRASTRUCTURE` is not something support may touch, and `KNOWN_INCIDENT` means stop
 * diagnosing. A single "contact support" would collapse all nine.
 */
export const SUPPORT_CLASSES = [
  'USER/HOW-TO',
  'UX/MISUNDERSTANDING',
  'PERMISSION/ROLE',
  'CONFIGURATION',
  'CONNECTOR/SOURCE',
  'DATA_FRESHNESS/UPSTREAM',
  'OPERATIONS/INFRASTRUCTURE',
  'KNOWN_INCIDENT',
] as const;

/**
 * `BUG_CANDIDATE` IS ABSENT FROM THAT LIST ON PURPOSE. It is the ninth class and the one J.0 calls
 * *"the most expensive thing a support surface can say"*. This panel may not reach it.
 */
export type SupportClass = (typeof SUPPORT_CLASSES)[number];

export interface GuideEntry {
  /** What the person SAW, in their words — view 2's rule for how a symptom is written. */
  readonly symptom: Bilingual;
  readonly supportClass: SupportClass;
  /** What is actually happening. Never a guess; every one of these is source-proven. */
  readonly because: Bilingual;
  /** What to do now. Always an action the reader can take, or the person who can. */
  readonly next: Bilingual;
  /**
   * Hidden unless the session holds this capability. See the header: telling somebody how to do
   * something the gate will refuse is an answer that costs them an afternoon.
   */
  readonly needs?: string;
  /**
   * WHERE `next` SENDS THEM, when it sends them anywhere.
   *
   * The Owner: *"the customer asks a question or picks a category, and can click the link and it
   * takes them to the right page."* An instruction that names a screen and leaves the reader to
   * find it is half an answer -- so where `next` says "grant it on the Company access screen",
   * this is that screen, and the panel draws the sentence as a button.
   *
   * Omitted where the action is on THIS screen (a link back to where they already are is noise)
   * or where there is no screen to send them to. A value here must come from `routes`:
   * `supportLinks.test.ts` refuses a typed path, because a help link that 404s is worse than none.
   */
  readonly href?: string;
}

export interface ScreenGuide {
  /** `AppShell`'s `screenId`. A screen with no entry gets the honest no-answer state. */
  readonly screenId: string;
  readonly what: Bilingual;
  readonly entries: readonly GuideEntry[];
}

/**
 * ── THE SCREENS THIS BUILD CAN ANSWER FOR ───────────────────────────────────────────────────────
 *
 * Deliberately short. Every entry below is something this round PROVED — most of them are defects
 * the Owner hit and their resolutions — rather than a plausible paragraph about a screen nobody
 * has driven. `K_KNOWLEDGE_LIFECYCLE.md` is the process by which this grows; inventing rows to
 * look complete is what it exists to prevent.
 */
export const SCREEN_GUIDES: readonly ScreenGuide[] = [
  {
    screenId: 'tenant-settings',
    what: {
      he: 'הגדרות הארגון: מי עובד כאן, מה מותר לכל אחד, אילו חברות יש, ומאיפה מגיעים הנתונים.',
      en: 'Office settings: who works here, what each of them may do, which companies exist, and where their data comes from.',
    },
    entries: [
      {
        symptom: {
          he: 'שמרתי את שם הארגון, קיבלתי הודעת הצלחה, והשם לא מופיע.',
          en: 'I saved the office name, got a success message, and the name is not there.',
        },
        supportClass: 'UX/MISUNDERSTANDING',
        because: {
          he: 'שם הארגון נשמר בשורה נפרדת, והכותרת קוראת אותו מתוך ההתחברות. עד 2026-10-26 התצוגה לא התעדכנה עד התחברות הבאה.',
          en: 'The office name is stored in its own row, and the header reads it from the session. Until 2026-10-26 the display did not catch up until the next sign-in.',
        },
        next: {
          he: 'מהגרסה הזאת התצוגה מתעדכנת מיד עם השמירה. אם השם עדיין אינו מופיע — זו תקלה, ויש לפנות עם קוד הפנייה.',
          en: 'From this release the display updates as soon as you save. If the name is still missing, that is a fault — report it with the reference code.',
        },
        needs: 'tenant.branding.manage',
      },
      {
        symptom: {
          he: 'אני לא מוצא איפה מגדירים מי רשאי לפתוח חברה.',
          en: 'I cannot find where to say who may open a company.',
        },
        supportClass: 'USER/HOW-TO',
        because: {
          he: 'תפקיד קובע מה מותר לעשות; גישה לחברה היא הענקה נפרדת לכל חברה. אלה שני מסכים שונים בכוונה.',
          en: 'A role says what somebody may do; access to a company is granted separately, per company. They are two screens on purpose.',
        },
        next: {
          he: 'הרשאות לחברות, תחת ״חברות ולקוחות״. תפקיד לעולם אינו מקנה גישה לחברה.',
          en: 'Company access, under “Companies and clients”. A role never grants access to a company.',
        },
        href: routes.tenantCompanyAccess,
      },
    ],
  },
  {
    screenId: 'tenant-users',
    what: {
      he: 'משתמשי הארגון: מי קיים, מי הוזמן וטרם הצטרף, ומי הושבת.',
      en: 'The office’s people: who exists, who was invited and has not joined, and who was disabled.',
    },
    entries: [
      {
        symptom: {
          he: 'מנהל הארגון שכבר התחבר אינו ברשימה, או שהמסך לא נטען.',
          en: 'The administrator who already signed in is not in the list, or the screen did not load.',
        },
        supportClass: 'KNOWN_INCIDENT',
        because: {
          he: 'עד 2026-10-26 המסך קרס בזמן הרינדור בגלל שדה שהשרת שלח בשם אחר, ולכן אף שורה לא הוצגה. המשתמש תמיד היה בתשובה.',
          en: 'Until 2026-10-26 the screen threw during render because the server sent a field under a different name, so no row was painted. The person was always in the answer.',
        },
        next: {
          he: 'מתוקן בגרסה הזאת. אין ליצור חשבון נוסף לאותו אדם — הוא כבר קיים.',
          en: 'Fixed in this release. Do not create a second account for the same person — they already exist.',
        },
      },
      {
        symptom: {
          he: 'הזמנתי משתמש והמייל לא הגיע.',
          en: 'I invited someone and the e-mail never arrived.',
        },
        supportClass: 'CONFIGURATION',
        /*
         * ══ REWRITTEN 2026-10-29: THE OLD ANSWER WAS WRONG ABOUT THIS BUILD. ══════════════════
         *
         * It said the submission connection *"is not configured in this build"* and that the
         * message *"is written to a file instead of being sent"*. `smtp-config.ts` and
         * `smtp-transport.ts` exist: on an estate carrying the four GLPOINT_MAIL_SMTP_* keys the
         * message is submitted, and this panel was telling the reader it was not. It also named
         * the sending address and the provider in a string literal -- the hard-coded address
         * §12.11 asks about -- and asserted an estate configuration the panel cannot observe.
         *
         * So it now names BOTH states and the one observable thing that separates them, and says
         * outright that it cannot see this message. That is rule 2 of this file's header.
         */
        because: {
          he: 'שליחת דואר תלויה בהגדרת חיבור השליחה של המערכת. ללא חיבור מוגדר, ההזמנה נוצרת ונרשמת אבל ההודעה נכתבת לקובץ ואינה נשלחת; עם חיבור מוגדר, ההודעה נשלחה ועיין נדחתה או סוננה אצל הנמען. **אינני רואה מה קרה להודעה הזאת** — מצב ערוץ הדואר של המערכת מוצג במסך תקינות הפלטפורמה.',
          en: 'Outbound mail depends on the estate\'s submission connection. With none configured the invitation is created and recorded but the message is written to a file and never sent; with one configured it was submitted and may have been rejected or filtered at the recipient. **I cannot see what happened to this particular message** — the estate\'s mail channel is shown on the platform health screen.',
        },
        next: {
          he: 'העבר את קישור ההזמנה ידנית כדי להמשיך עכשיו, ובקש ממי שמנהל את המערכת לבדוק את מצב ערוץ הדואר. כתובת השליחה היא הגדרת מערכת — שינויה אינו דורש שינוי קוד.',
          en: 'Pass the invitation link on by hand to keep moving now, and ask whoever administers the estate to check the mail channel. The sending address is estate configuration — changing it needs no code change.',
        },
      },
    ],
  },
  {
    screenId: 'tenant-company',
    what: {
      he: 'כרטיס החברה: הפרטים, מערכת הנהלת החשבונות, החיבור, ההרשאות ומצב החברה.',
      en: 'The company record: its details, its accounting system, its connection, its access and its lifecycle.',
    },
    entries: [
      {
        symptom: {
          he: 'הזנתי ח״פ בהקמה והרשימה מציגה ״ללא ח.פ רשום״.',
          en: 'I entered a registration number and the list says there is none.',
        },
        supportClass: 'KNOWN_INCIDENT',
        because: {
          he: 'המספר נשמר תמיד, ועד 2026-10-26 שום קריאה לא החזירה אותו לדפדפן.',
          en: 'The number was always stored, and until 2026-10-26 no read returned it to the browser.',
        },
        next: {
          he: 'מתוקן בגרסה הזאת. אין להקים את החברה מחדש.',
          en: 'Fixed in this release. Do not re-create the company.',
        },
      },
      {
        symptom: {
          he: 'פתיחת החברה לעבודה נדחית אף שאני מנהל הארגון.',
          en: 'Opening the company is refused even though I administer the office.',
        },
        supportClass: 'PERMISSION/ROLE',
        because: {
          he: 'גישה לחברה היא הענקה מפורשת לכל חברה, גם לבעלים של הארגון. חברה שהוקמה זה עתה אינה נגישה לאיש עד שתוענק גישה.',
          en: 'Access to a company is granted explicitly, per company, even to the office owner. A company created a moment ago is open to nobody until access is granted.',
        },
        next: {
          he: 'הענק גישה במסך ״הרשאות לחברות״. זו אינה תקלה אלא ההסדר שמונע מהרשאה להיווצר מאליה.',
          en: 'Grant it on the Company access screen. This is not a fault; it is what stops access appearing by itself.',
        },
        href: routes.tenantCompanyAccess,
        needs: 'companies.assignment.manage',
      },
    ],
  },
  {
    screenId: 'tenant-sources',
    what: {
      he: 'מקורות נתונים: החיבורים למערכות חיצוניות, מצבם וטריות הנתונים שמגיעים מהם.',
      en: 'Data sources: the connections to external systems, their state, and how current their data is.',
    },
    entries: [
      {
        symptom: {
          he: 'הגדרתי חיבור ולא מגיעים נתונים.',
          en: 'I defined a connection and no data arrives.',
        },
        supportClass: 'CONNECTOR/SOURCE',
        because: {
          he: 'בגרסה הזאת אין תוכנת חיבור לאף מערכת הנהלת חשבונות. ההגדרה נשמרת ונרשמת; משיכה בפועל אינה מתרחשת.',
          en: 'This build carries no connector software for any accounting system. The definition is stored and audited; nothing is retrieved from the system.',
        },
        next: {
          he: 'בדיקת חיבור תאמר זאת בשם. חיבור אמיתי דורש אינטגרציה מול הספק — החלטה של בעל המוצר.',
          en: 'A connection test says so by name. A real connection needs a vendor integration — an Owner decision.',
        },
      },
    ],
  },
];

/** The guide for a screen, or `null` — which the panel renders as a stated absence. */
export function guideFor(screenId: string | undefined): ScreenGuide | null {
  if (screenId === undefined) return null;
  return SCREEN_GUIDES.find((guide) => guide.screenId === screenId) ?? null;
}

/**
 * The entries this session may be shown.
 *
 * J.3 in one function: an entry describing an action the caller cannot perform is not an answer,
 * and showing it leaks that the action exists to somebody the gate refuses.
 */
export function visibleEntries(
  guide: ScreenGuide,
  capabilities: readonly string[],
): readonly GuideEntry[] {
  return guide.entries.filter(
    (entry) => entry.needs === undefined || capabilities.includes(entry.needs),
  );
}
